import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Star,
  Plus,
  ArrowDownToLine,
  ShieldCheck,
  Zap,
  CheckCircle2,
  Loader2,
  Clock,
  Sparkles,
  AlertCircle,
  Layers,
  ShoppingBag,
} from 'lucide-react';
import { formatCoinAmount } from '@nova-mine/shared';
import { MiningProgressRing } from '../components/common/MiningProgressRing.js';
import { AnimatedCoinCounter } from '../components/common/AnimatedCoinCounter.js';
import { GlassCard } from '../components/common/GlassCard.js';
import { GradientCard } from '../components/common/GradientCard.js';
import { AnimatedButton } from '../components/common/AnimatedButton.js';
import { MinerStatusBadge } from '../components/common/MinerStatusBadge.js';
import { NovaCoin3D } from '../components/common/NovaCoin3D.js';
import { sound } from '../utils/sound.js';
import { api } from '../services/api.js';

export interface StarsMinerScreenProps {
  starsUnits: number;
  starsAccrued: number;
  minersList: Array<{
    id: string | number;
    units: number;
    stars_amount: number;
    purchased_at: string | number;
    daily_rate?: number;
    claimable?: number;
    activated_at?: number;
    last_claimed_at?: number;
    total_claimed?: number;
    status?: string;
  }>;
  lastClaimedAtMs: number;
  isClaiming: boolean;
  claimSuccessMessage: string | null;
  onOpenShop: () => void;
  onClaimAllStars: () => Promise<any>;
  onClaimSingleMiner: (minerId: string) => Promise<any>;
  onTriggerFloatingCoins: () => void;
  openInvoice: (invoiceUrl: string) => Promise<string>;
  onPurchaseSuccess: () => Promise<any>;
  onMinerActivated?: () => void;
  accountBalance?: number;
  isDemoMode?: boolean;
}

export type PurchaseStage =
  | 'idle'
  | 'creating_invoice'
  | 'awaiting_payment'
  | 'confirming'
  | 'confirmed'
  | 'cancelled'
  | 'failed';

export const StarsMinerScreen: React.FC<StarsMinerScreenProps> = ({
  starsUnits,
  starsAccrued,
  minersList,
  lastClaimedAtMs,
  isClaiming,
  claimSuccessMessage,
  onOpenShop,
  onClaimAllStars,
  onClaimSingleMiner,
  onTriggerFloatingCoins,
  openInvoice,
  onPurchaseSuccess,
  onMinerActivated,
  accountBalance = 0,
  isDemoMode = false,
}) => {
  const [purchaseStage, setPurchaseStage] = useState<PurchaseStage>('idle');
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const [claimingMinerId, setClaimingMinerId] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState<number>(Date.now());

  // Real-time second ticker for live individual miner progress
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const dailyTotalRate = starsUnits * 100000;
  const ratePerSecond = dailyTotalRate / 86400;
  const canClaimAll = starsAccrued >= 0.0001;

  // 1. Direct Purchase Flow for "Buy Miner — 10 ⭐"
  const handleDirectBuy = async () => {
    if (isDemoMode) {
      alert(
        'SANDBOX MODE: Telegram Stars invoice creation requires live bot configuration. Connect your Cloudflare Worker to enable production checkout.'
      );
      return;
    }

    if (
      purchaseStage === 'creating_invoice' ||
      purchaseStage === 'awaiting_payment' ||
      purchaseStage === 'confirming'
    ) {
      return;
    }

    try {
      setPurchaseError(null);
      setPurchaseStage('creating_invoice');

      // 1. Create persistent Stars order in backend (1 unit = 10 Stars)
      const invoiceRes = await api.createStarsInvoice(1);
      if (!invoiceRes.order_id || !invoiceRes.invoice_link) {
        throw new Error('Invoice link could not be generated by backend');
      }

      const orderId = invoiceRes.order_id;
      setActiveOrderId(orderId);
      setPurchaseStage('awaiting_payment');

      // 2. Open supported Telegram Mini App invoice API
      const paymentStatus = await openInvoice(invoiceRes.invoice_link);

      if (paymentStatus === 'cancelled') {
        await api.cancelStarsOrder(orderId).catch(() => {});
        setPurchaseStage('cancelled');
        setPurchaseError('Payment was cancelled in Telegram.');
      } else if (paymentStatus === 'failed') {
        setPurchaseStage('failed');
        setPurchaseError('Telegram payment was declined or failed.');
      } else {
        // Status is 'paid', 'pending', or dialog closed:
        // Client-side callback alone is NOT sufficient proof!
        // Authoritatively poll backend order status
        setPurchaseStage('confirming');

        let attempts = 0;
        const maxAttempts = 12; // 12 * 1.5s = 18s poll
        const pollInterval = setInterval(async () => {
          attempts++;
          try {
            const statusRes = await api.checkStarsOrderStatus(orderId);
            if (statusRes.status === 'paid' && Boolean(statusRes.miner_id)) {
              clearInterval(pollInterval);
              setPurchaseStage('confirmed');
              sound.playUnlockSurge();
              onTriggerFloatingCoins();
              if (onMinerActivated) onMinerActivated();
              await onPurchaseSuccess();
            } else if (statusRes.status === 'cancelled' || statusRes.status === 'failed') {
              clearInterval(pollInterval);
              setPurchaseStage(statusRes.status);
              setPurchaseError('Payment was cancelled or rejected by Telegram.');
            } else if (attempts >= maxAttempts) {
              clearInterval(pollInterval);
              setPurchaseStage('idle');
              setPurchaseError('Payment confirmation timed out. If payment succeeded, your miner will activate automatically.');
            }
          } catch {
            if (attempts >= maxAttempts) {
              clearInterval(pollInterval);
              setPurchaseStage('idle');
            }
          }
        }, 1500);
      }
    } catch (err: unknown) {
      setPurchaseStage('failed');
      setPurchaseError(err instanceof Error ? err.message : 'Failed to create invoice');
    }
  };

  const handleClaimSingle = async (minerId: string) => {
    if (claimingMinerId !== null || isClaiming) return;
    try {
      setClaimingMinerId(minerId);
      await onClaimSingleMiner(minerId);
      sound.playClaimSuccess();
      onTriggerFloatingCoins();
    } catch {
      // Handled in parent
    } finally {
      setClaimingMinerId(null);
    }
  };

  return (
    <div className="w-full space-y-5 animate-fade-in pb-4">
      {/* 1. VIP Stars Fleet Hero Card */}
      <GlassCard variant="gold" className="border-amber-500/40 p-6 relative overflow-hidden">
        {/* Background Decorative Coin */}
        <div className="absolute -right-6 -bottom-6 opacity-20 pointer-events-none">
          <NovaCoin3D size="2xl" />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="w-2.5 h-2.5 rounded-full bg-nova-gold animate-ping" />
            <span className="text-[11px] font-black uppercase tracking-widest text-amber-200">
              VIP High-Yield Stars Fleet
            </span>
          </div>

          <span className="text-[10px] font-mono font-extrabold px-2.5 py-0.5 rounded-full bg-amber-400/20 text-nova-gold border border-amber-400/35">
            {starsUnits} Active Unit{starsUnits !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Fleet Accrual Stat Display */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-baseline justify-between gap-2">
          <div>
            <span className="text-xs text-gray-400 uppercase font-bold tracking-wider block">
              Fleet Unclaimed Balance
            </span>
            <div className="flex items-baseline space-x-2 mt-1">
              <AnimatedCoinCounter
                value={starsAccrued}
                ratePerSecond={ratePerSecond}
                decimals={4}
                className="text-3xl sm:text-4xl font-black font-mono text-gold-gradient tracking-tight"
              />
              <span className="text-xs font-black text-amber-300 font-mono">NOVA</span>
            </div>
          </div>

          <div className="text-right">
            <span className="text-xs text-gray-400 uppercase font-bold tracking-wider block">
              Combined Hash Output
            </span>
            <span className="text-lg font-black font-mono text-white mt-0.5 block">
              +{dailyTotalRate.toLocaleString()} / 24h
            </span>
          </div>
        </div>

        {/* Multi-Rig Claim All & Shop Buttons */}
        <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <AnimatedButton
            variant="gold"
            size="md"
            onClick={onClaimAllStars}
            disabled={!canClaimAll || isClaiming}
            isLoading={isClaiming}
            icon={<ArrowDownToLine className="w-4 h-4" />}
            className="w-full justify-center"
          >
            Claim All Fleet Coins
          </AnimatedButton>

          <AnimatedButton
            variant="purple"
            size="md"
            onClick={onOpenShop}
            icon={<ShoppingBag className="w-4 h-4" />}
            className="w-full justify-center"
          >
            Multi-Rig Shop
          </AnimatedButton>
        </div>
      </GlassCard>

      {/* 2. Direct 10 ⭐ Purchase Action Banner */}
      <GradientCard gradientTheme="gold" className="border-amber-400/40">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-3.5 w-full sm:w-auto">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 to-yellow-400 flex items-center justify-center text-midnight-950 font-black text-xl shadow-lg shadow-amber-500/30 flex-shrink-0">
              <Star className="w-7 h-7 fill-current text-midnight-950" />
            </div>

            <div>
              <div className="flex items-center space-x-2">
                <h4 className="text-sm font-black text-white">Deploy 1x Stars Miner</h4>
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-amber-400/25 text-nova-gold border border-amber-400/40">
                  Fixed 10 ⭐
                </span>
              </div>
              <p className="text-xs text-amber-200/80 mt-0.5">
                Yields <span className="font-mono font-bold text-white">+100,000 coins / 86,400s</span>
              </p>
            </div>
          </div>

          <div className="w-full sm:w-auto">
            <AnimatedButton
              variant="gold"
              size="lg"
              onClick={handleDirectBuy}
              disabled={
                purchaseStage === 'creating_invoice' ||
                purchaseStage === 'awaiting_payment' ||
                purchaseStage === 'confirming'
              }
              isLoading={
                purchaseStage === 'creating_invoice' ||
                purchaseStage === 'awaiting_payment' ||
                purchaseStage === 'confirming'
              }
              icon={<Star className="w-4 h-4 fill-current" />}
              className="w-full sm:w-auto justify-center"
            >
              {purchaseStage === 'creating_invoice'
                ? 'Creating Invoice...'
                : purchaseStage === 'awaiting_payment'
                ? 'Awaiting Payment...'
                : purchaseStage === 'confirming'
                ? 'Verifying Ledger...'
                : 'Buy Miner — 10 ⭐'}
            </AnimatedButton>
          </div>
        </div>

        {/* Purchase Status Notifications */}
        <AnimatePresence>
          {purchaseStage === 'confirmed' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 p-3 rounded-xl bg-emerald-500/20 border border-emerald-500/35 text-emerald-300 text-xs flex items-center space-x-2"
            >
              <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span>Purchase confirmed! New VIP Stars Miner deployed to your active fleet.</span>
            </motion.div>
          )}

          {purchaseError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 p-3 rounded-xl bg-rose-500/20 border border-rose-500/35 text-rose-300 text-xs flex items-center space-x-2"
            >
              <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
              <span>{purchaseError}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </GradientCard>

      {/* 3. Individual Purchased Miner Fleet Cards */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <h4 className="text-xs font-black uppercase tracking-wider text-gray-300 flex items-center space-x-2">
            <Layers className="w-3.5 h-3.5 text-nova-gold" />
            <span>Individually Deployed Rigs ({minersList.length})</span>
          </h4>
          <span className="text-[11px] font-mono text-nova-gold font-bold">
            100k coins / 24h each
          </span>
        </div>

        {minersList.length === 0 ? (
          <GlassCard variant="deep" className="text-center py-8">
            <Star className="w-10 h-10 text-gray-600 mx-auto mb-2" />
            <h5 className="text-sm font-bold text-gray-300">No Stars Miners Active</h5>
            <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">
              Deploy your first 100,000/24h Stars Miner above with Telegram Stars to accelerate your hash output.
            </p>
          </GlassCard>
        ) : (
          <div className="space-y-3">
            {minersList.map((m, idx) => {
              const minerId = String(m.id);
              const activatedAt = Number(m.activated_at || m.purchased_at || 0);
              const lastClaimed = Number(m.last_claimed_at || activatedAt);
              const totalClaimed = Number(m.total_claimed || 0);
              const rateSec = (100000 * Number(m.units || 1)) / 86400;

              // Authoritative calculation
              const elapsedActiveSec = Math.max(0, (nowMs - activatedAt) / 1000);
              const cumulative = Math.floor((elapsedActiveSec * 100000 * Number(m.units || 1)) / 86400);
              const currentClaimable = Math.max(0, cumulative - totalClaimed);

              const elapsedMs = Math.max(0, nowMs - lastClaimed);
              const progressPct = Math.min(100, Math.round(((elapsedMs / 1000) % 86400) / 864));
              const isThisClaiming = claimingMinerId === minerId;

              return (
                <motion.div
                  key={minerId}
                  initial={{ opacity: 0, y: 16, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.35, delay: idx * 0.05, ease: 'easeOut' }}
                >
                  <GlassCard variant="gold" className="p-4 border-amber-500/25 shimmer-sweep">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center space-x-3.5">
                      {/* Individual Mini Progress Ring */}
                      <MiningProgressRing
                        size={52}
                        strokeWidth={4}
                        progressPercent={progressPct}
                        colorScheme="gold"
                      >
                        <Star className="w-5 h-5 text-nova-gold fill-current" />
                      </MiningProgressRing>

                      <div>
                        <div className="flex items-center space-x-2">
                          <h5 className="font-black text-white text-xs">
                            Stars Miner #{idx + 1}
                          </h5>
                          <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-amber-400/20 text-nova-gold font-bold">
                            {m.units}x Rig
                          </span>
                        </div>

                        <div className="mt-1 flex items-baseline space-x-1.5">
                          <span className="text-sm font-black font-mono text-gold-gradient">
                            {formatCoinAmount(currentClaimable, 2)}
                          </span>
                          <span className="text-[10px] text-amber-300 font-bold">NOVA</span>
                          <span className="text-[10px] text-gray-400 ml-2">
                            • +{(rateSec).toFixed(4)}/s
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <AnimatedButton
                        variant="gold"
                        size="sm"
                        onClick={() => handleClaimSingle(minerId)}
                        disabled={currentClaimable < 0.0001 || isThisClaiming}
                        isLoading={isThisClaiming}
                        icon={<ArrowDownToLine className="w-3.5 h-3.5" />}
                      >
                        Claim
                      </AnimatedButton>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            );
          })}
          </div>
        )}
      </div>
    </div>
  );
};
